import { findScrollableParent } from '@thiememeulenhoff/js-sdk';
import gsap from 'gsap';
import { ReactiveController, ReactiveControllerHost } from 'lit';
import { DraggableComponent } from './draggable.component';

/*
 * This controller implements a custom auto-scrolling behavior for draggable elements
 * when they are dragged near the edges of a scrollable container.
 * Normally GSAP handles dragging and scrolling, but GSAP does not support auto-scrolling with nested shadow DOM elements.
 * This controller uses IntersectionObserver to detect when the draggable element is near the edges of the scrollable container
 * and then scrolls the container accordingly.
 * It also prevents default scrolling behavior on the window to avoid conflicts.
 */
export class DraggableScrollController implements ReactiveController {
  host: ReactiveControllerHost & DraggableComponent;

  #scrollInterval = 0; // setInterval id for cleanup

  #scrollSpeed = 10; // 10px each frame

  #scrollTimeout = 1000 / 24; // 24 fps

  #relativeScroll = 0; // current scroll position

  #autoScrollMargin = 50; // margin for auto-scrolling

  #intersectionObserverOptions = {
    root: null,
    rootMargin: `-${this.#autoScrollMargin}px 0px`,
    threshold: 0,
  };

  #intersectionObserver = new IntersectionObserver(
    this.#intersectionObserverCallback.bind(this),
    this.#intersectionObserverOptions,
  );

  constructor(host: ReactiveControllerHost & DraggableComponent) {
    (this.host = host).addController(this);
  }

  hostDisconnected() {
    this.cleanupGsapDisabledStrategy();
  }

  gsapAutoscrollDisabledStrategy() {
    this.#intersectionObserver.observe(this.host);
    window.addEventListener('wheel', this.#preventScroll, { passive: false });
    window.addEventListener('touchmove', this.#preventScroll, {
      passive: false,
    });
    window.addEventListener('keydown', this.#preventKeydown);
  }

  #intersectionObserverCallback(entries: Array<IntersectionObserverEntry>) {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) {
        const scrollDirection = this.#getScrollDirection(entry);
        if (scrollDirection) {
          this.#handleScroll(this.host, scrollDirection);
        }
      } else if (this.#scrollInterval) {
        window.clearInterval(this.#scrollInterval);
      }
    });
  }

  #getScrollDirection(
    entry: IntersectionObserverEntry,
  ): 'top' | 'bottom' | null {
    const intersectTop =
      entry.boundingClientRect.y <=
      (entry.rootBounds?.top ?? 0) + this.#autoScrollMargin;
    const intersectBottom =
      entry.boundingClientRect.y >= this.#autoScrollMargin;

    if (intersectTop) {
      return 'top';
    }
    if (intersectBottom) {
      return 'bottom';
    }
    return null;
  }

  #handleScroll(element: HTMLElement, direction: 'top' | 'bottom') {
    const parent = findScrollableParent(element);
    this.#scrollInterval = window.setInterval(() => {
      if (!parent) {
        return;
      }

      if (direction === 'top') {
        parent.scrollTop -= this.#scrollSpeed;
        this.#relativeScroll = -this.#scrollSpeed;
      }
      if (direction === 'bottom') {
        parent.scrollTop += this.#scrollSpeed;
        this.#relativeScroll = this.#scrollSpeed;
      }

      if (
        parent.scrollTop !== 0 &&
        parent.scrollTop < parent.scrollHeight - parent.clientHeight
      ) {
        const yPosition = this.#getYPositionFromTransform();
        gsap.to(this.host, {
          y: yPosition + this.#relativeScroll,
          duration: 0,
        });
      }
    }, this.#scrollTimeout);
  }

  #getYPositionFromTransform() {
    const { transform } = this.host.style;
    if (!transform || transform === 'none') {
      return 0;
    }

    // Match translate3d(x, y, z)
    const matchTranslate3d = transform.match(/translate3d\([^,]+,\s*([^)]+),/);
    if (matchTranslate3d) {
      return parseFloat(matchTranslate3d[1]);
    }

    return 0;
  }

  cleanupGsapDisabledStrategy() {
    if (this.#scrollInterval) {
      window.clearInterval(this.#scrollInterval);
      this.#relativeScroll = 0;
    }
    this.#intersectionObserver.disconnect();
    window.removeEventListener('wheel', this.#preventScroll);
    window.removeEventListener('touchmove', this.#preventScroll);
    window.removeEventListener('keydown', this.#preventKeydown);
  }

  #preventScroll = (e: Event) => e.preventDefault();

  #preventKeydown = (e: KeyboardEvent) => {
    const keys = ['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Space'];
    if (keys.includes(e.code)) {
      e.preventDefault();
    }
  };
}
